/**
 * Shared helpers for custom target create payloads.
 * UI tab modes map to API selection: "scripts" | "json_parameters" | "auto_learn".
 */

export const CUSTOM_TARGET_PY_SCRIPTS = "custom_target_py_scripts";
export const CUSTOM_TARGET_USER_ENTERED_JSONS = "custom_target_user_entered_jsons";
export const CUSTOM_TARGET_ADVANCED = "custom_target_advanced";

function parseJsonField(text, fallback) {
  if (text && typeof text === "object") return text;
  const trimmed = String(text || "").trim();
  if (!trimmed) return fallback;
  try {
    return JSON.parse(trimmed);
  } catch {
    return trimmed;
  }
}

function isJsonObject(value) {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

/** Parsed JSON object, or {} when the text is empty, invalid, or not an object. */
function parseJsonOrEmpty(text) {
  if (isJsonObject(text)) return text;
  const trimmed = String(text ?? "").trim();
  if (!trimmed) return {};
  try {
    const parsed = JSON.parse(trimmed);
    return isJsonObject(parsed) ? parsed : {};
  } catch {
    return {};
  }
}

function jsonTextOrEmpty(value) {
  const text = prettyJson(value);
  if (!text.trim()) return "{}";
  try {
    JSON.parse(text);
    return text;
  } catch {
    return "{}";
  }
}

function parseStringList(text) {
  if (Array.isArray(text)) return text;
  const trimmed = String(text || "").trim();
  if (!trimmed) return [];
  try {
    const parsed = JSON.parse(trimmed);
    if (Array.isArray(parsed)) return parsed.map((item) => String(item));
  } catch {
    // Comma-separated text is the editable form.
  }
  return trimmed
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);
}

function commonContract(preferredInterface, options) {
  const contract = { transport: preferredInterface };
  if (preferredInterface !== "browser_ui") {
    contract.request_template_json = parseJsonOrEmpty(options.request_template_json);
    contract.response_template_json = parseJsonOrEmpty(options.response_template_json);
  }
  return contract;
}

/** Advanced-tab values -> preferred_interface and preferred_contract. */
function buildAdvParams(options = {}, url = "") {
  const preferredInterface = options.preferred_interface || "browser_ui";
  const preferred_contract = commonContract(preferredInterface, options);

  if (preferredInterface === "browser_ui") {
    preferred_contract.browser_mode =
      options.browser_mode === "interactive" ? "interactive" : "non_interactive";
    if (options.google_auth_info_required && isJsonObject(options.google_auth_info)) {
      preferred_contract.google_auth_info = options.google_auth_info;
    }
    if (options.login_required) {
      preferred_contract.username = options.username || "";
      preferred_contract.password = options.password || "";
    }
  }

  const websocket = preferredInterface === "websocket" || preferredInterface === "json_rpc_websocket";
  const http =
    preferredInterface === "json_rpc_http" ||
    preferredInterface === "graphql" ||
    preferredInterface === "sse";

  if (websocket || http) {
    preferred_contract.execution_method = {
      interface_type: preferredInterface,
      url,
      ...(http ? { method: options.method || "" } : {}),
      headers: parseJsonField(options.headers, {}),
      prompt_key_path: parseStringList(options.prompt_key_path),
      response_key_path: parseStringList(options.response_key_path),
      ...(preferredInterface === "sse"
        ? { stream_end_markers: parseStringList(options.stream_end_markers) }
        : {}),
    };
  }

  return { preferred_interface: preferredInterface, preferred_contract };
}

export function prettyJson(value) {
  if (value == null || value === "") return "";
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return "";
    try {
      return JSON.stringify(JSON.parse(trimmed), null, 2);
    } catch {
      return value;
    }
  }
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return "";
  }
}

function listToText(value) {
  if (Array.isArray(value)) return value.map((item) => String(item)).join(", ");
  if (value == null || value === "") return "";
  return String(value);
}

/** Saved auto_learn params -> Advanced tab field values. */
export function advancedOptionsFromTargetParams(params = {}) {
  const contract =
    params.preferred_contract && typeof params.preferred_contract === "object"
      ? params.preferred_contract
      : {};
  const execution =
    contract.execution_method && typeof contract.execution_method === "object"
      ? contract.execution_method
      : {};
  const method =
    contract.method != null && contract.method !== ""
      ? contract.method
      : execution.method;

  return {
    preferred_interface: params.preferred_interface || contract.transport || "browser_ui",
    request_template_json: jsonTextOrEmpty(contract.request_template_json),
    response_template_json: jsonTextOrEmpty(contract.response_template_json),
    method: method != null && method !== "" ? String(method) : "",
    browser_mode:
      contract.browser_mode === "interactive" ? "interactive" : "non_interactive",
    google_auth_info_required: Object.prototype.hasOwnProperty.call(
      contract,
      "google_auth_info"
    ),
    google_auth_info: Object.prototype.hasOwnProperty.call(contract, "google_auth_info")
      ? isJsonObject(contract.google_auth_info)
        ? contract.google_auth_info
        : parseJsonOrEmpty(contract.google_auth_info)
      : null,
    login_required:
      Object.prototype.hasOwnProperty.call(contract, "username") ||
      Object.prototype.hasOwnProperty.call(contract, "password"),
    username: contract.username != null ? String(contract.username) : "",
    password: contract.password != null ? String(contract.password) : "",
    headers: prettyJson(execution.headers),
    prompt_key_path: listToText(execution.prompt_key_path),
    response_key_path: listToText(execution.response_key_path),
    stream_end_markers: listToText(execution.stream_end_markers),
  };
}

export function selectionToApiConfigMode(selection) {
  if (selection === "json_parameters") return CUSTOM_TARGET_USER_ENTERED_JSONS;
  if (selection === "auto_learn") return CUSTOM_TARGET_ADVANCED;
  return CUSTOM_TARGET_PY_SCRIPTS;
}

/**
 * getTargetConfigById response -> create-form state.
 * Former predefined targets (creation_mode "predefined") open as custom JSON.
 */
export function mapTargetConfigToForm(data = {}) {
  const rawParams = data.target_params;
  const params =
    rawParams && typeof rawParams === "object" ? rawParams : {};
  const selection = params.selection || "";
  const targetType = data.target_type || "api_target";
  const model = params.model_details && typeof params.model_details === "object"
    ? params.model_details
    : {};
  const jsonParams =
    data.json_parameters && typeof data.json_parameters === "object"
      ? data.json_parameters
      : {};

  let llmTab = "custom-targets";
  if (selection === "model") llmTab = "existing-models";
  else if (selection === "simulator") llmTab = "simulators";

  return {
    target: {
      name: data.target_name || "",
      description: data.target_desc || "",
      target_type: targetType,
      url: params.url || "",
      apiConfigMode: selectionToApiConfigMode(selection),
      requestParams: prettyJson(jsonParams.request),
      responseParams: prettyJson(jsonParams.response),
      input_script_prompt_processing: params.input_script_prompt_processing || "single",
    },
    llmTab,
    provider: model.provider || "",
    modelVersion: model.version || "",
    apiKey: model.api_key || "",
    advancedSeed: selection === "auto_learn" ? advancedOptionsFromTargetParams(params) : null,
    existingScripts:
      selection === "scripts"
        ? {
            input: typeof data.inputScript === "string" ? data.inputScript : "",
            output: typeof data.outputScript === "string" ? data.outputScript : "",
          }
        : { input: "", output: "" },
  };
}

export function minifyJSON(jsonString) {
  if (!jsonString?.trim()) return "";
  try {
    return JSON.stringify(JSON.parse(jsonString));
  } catch {
    return jsonString;
  }
}

/** Map UI tab mode to API selection. */
function resolveCustomTargetSelection(apiConfigMode) {
  if (apiConfigMode === CUSTOM_TARGET_PY_SCRIPTS) return "scripts";
  if (apiConfigMode === CUSTOM_TARGET_USER_ENTERED_JSONS) return "json_parameters";
  if (apiConfigMode === CUSTOM_TARGET_ADVANCED) return "auto_learn";
  return apiConfigMode;
}

/** @returns {{ selection: string, targetParams: object }} */
export function buildCustomTargetParams({
  url = "",
  apiConfigMode = CUSTOM_TARGET_PY_SCRIPTS,
  promptsProcessing = "single",
  advancedOptions = null,
} = {}) {
  const selection = resolveCustomTargetSelection(apiConfigMode);
  const targetParams = { url, selection, creation_mode: "custom" };
  if (selection === "scripts") {
    targetParams.input_script_prompt_processing = promptsProcessing;
  }
  if (selection === "auto_learn") {
    Object.assign(targetParams, buildAdvParams(advancedOptions || {}, url));
  }
  return { selection, targetParams };
}

/**
 * Append custom-target fields to FormData.
 * Callers still append target_name / target_desc / target_type.
 */
export function appendCustomTargetFormData(formData, options = {}) {
  const {
    requestParams = "",
    responseParams = "",
    inputScriptFile = null,
    outputScriptFile = null,
  } = options;

  const { selection, targetParams } = buildCustomTargetParams(options);

  if (selection === "json_parameters") {
    formData.append(
      "json_parameters",
      JSON.stringify({
        request: minifyJSON(requestParams),
        response: minifyJSON(responseParams),
      })
    );
  }

  formData.append("target_params", JSON.stringify(targetParams));

  if (selection === "scripts") {
    if (inputScriptFile) formData.append("inputScript", inputScriptFile);
    if (outputScriptFile) formData.append("outputScript", outputScriptFile);
  }

  return { selection, targetParams };
}
